import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../user/CartContext";
import { useCityPrice } from "../../hooks/useCityPrice";
import { isServiceEnabled, fetchRemoteConfig } from "../../config";
import { HOME_SERVICES } from "../../config/pricing";
import CategorySidebar from "./CategorySidebar";

import bathroom_cleaning_img        from "../../components/images/stage_01/bathroom_cleaning.png";
import complete_house_cleaning_img  from "../../components/images/stage_01/complete_house_cleaning.png";
import baby_caretaker_img           from "../../components/images/stage_01/baby_caretaker.png";
import medical_caretaker_img        from "../../components/images/stage_01/medical_caretaker.png";
import deep_cleaning_img            from "../../components/images/stage_01/deep_cleaning.png";
import home_utensils_cleaning_img   from "../../components/images/stage_01/home&utensils_Cleaning.png";
import vehicle_washing_extra_img    from "../../components/images/stage_01/vehicle_washing.png";
import gardening_extra_img          from "../../components/images/stage_01/gardening.png";
import livingroom_balcony_img        from "../../components/images/stage_01/livingroom_balcony.png";

// Quick Services Access parity — the same QAS_* cards used in the homepage's
// "Quick Services Access" row (HomePageContent.js), now also offered here as
// duration-sold Instant bookings (see isInstant below).
import qas_rangoli_img          from "../../components/images/stage_01/QAS_IHRA.png";
import qas_fish_tank_img        from "../../components/images/stage_01/QAS_IHFT.png";
import qas_showcase_img         from "../../components/images/stage_01/QAS_IHDC.png";
import qas_chimney_img          from "../../components/images/stage_01/QAS_IHCC.png";
import qas_microwave_oven_img   from "../../components/images/stage_01/QAS_IHMO.png";
import qas_kitchen_cabinet_img  from "../../components/images/stage_01/QAS_IHKC.png";
import qas_window_glass_img     from "../../components/images/stage_01/QAS_IHWG.png";
import qas_wardrobe_img         from "../../components/images/stage_01/QAS_IHWA.png";
import qas_bedroom_arrange_img  from "../../components/images/stage_01/QAS_IHBA.png";
import qas_outside_spaces_img   from "../../components/images/stage_01/QAS_IHBE.png";
import qas_fan_switchboard_img  from "../../components/images/stage_01/QAS_IHCF.png";
import qas_packing_unpacking_img from "../../components/images/stage_01/QAS_IHPU.png";
import qas_furniture_img        from "../../components/images/stage_01/QAS_IHFC.png";
import qas_dusting_wiping_img   from "../../components/images/stage_01/QAS_IHDW.png";
import qas_sweeping_mopping_img from "../../components/images/stage_01/QAS_IHSM.png";
import qas_food_prep_img        from "../../components/images/stage_01/QAS_IHFP.png";
import qas_utensil_img          from "../../components/images/stage_01/QAS_IHCU.png";
import qas_kitchen_cleaning_img from "../../components/images/stage_01/QAS_IHCK.png";
import qas_fridge_img           from "../../components/images/stage_01/QAS_IHRC.png";
import qas_balcony_img          from "../../components/images/stage_01/QAS_IHBC.png";
import qas_bathroom_img         from "../../components/images/stage_01/QAS_IHBT.png";

const fallbackImage = complete_house_cleaning_img;


// ─── Service data with details from config-style includes/excludes ────────────
export const services = [
  {
    // Bundled multi-room Instant booking — sold by duration (30-min blocks
    // up to 4 Hrs), not by room count, so it has no `link` (goes straight
    // through the direct-add path via handleBookInstantSpecial) and no
    // subServices (nothing further to pick — the 5 rooms are fixed).
    // Renamed from "Instant Special" to "Instant CN Pro" — same service,
    // same scope, name only.
    id: "hs-11",
    name: "Instant CN Pro",
    desc: "One visit, multiple rooms — pick how much time you need and we'll cover as much as we can.",
    ...HOME_SERVICES["hs-11"],
    image: complete_house_cleaning_img,
    images: [complete_house_cleaning_img],
    color: "#f43f5e",
    isSpecial: true,
    includes: [
      "Kitchen Cleaning",
      "Utensil Cleaning",
      "Food Preparation",
      "Sweeping & Mopping",
      "Dusting & Wiping",
      "Fan & Ceiling",
      "Laundry & Ironing",
      "Bathroom Cleaning",
      "Furniture Cleaning",
      "Bedroom Arrangement",
      "Wardrobe Arrangement",
      "Window and Glass",
      "Balcony & Entrance Cleaning",
      "Sand Paint / Rangoli / Floor Art",
    ],
    excludes: [
      "Deep / Restoration Cleaning",
      "Heavy Stains / Heavy Grease Removal",
      "Heavy Furniture Shifting / Dismantling",
      "Appliance Repair / Technical Work",
      "Plumbing / Electrical Work",
      "Heavy Utensil / Heavy Laundry Work",
      "Complex Cooking / Specialized Food Preparation",
      "Glass Repair / Replacement",
      "Paint / Cement / Permanent Surface Restoration",
      "High-Level, Unsafe or Unlisted Work",
    ],
    // Array form (bulleted list) — see customerNotes rendering in
    // DetailsModal (this file) and InstantServiceDetailPage.js, which
    // render either a single string (most services) or this list.
    customerNotes: [
      "CN Pro covers the listed Daily Basic Services only — additional, technical, heavy or specialized work requires a separate service.",
      "Book only the listed Daily Basic Services and keep the required work area accessible.",
      "For kitchen, utensil and food-preparation work, keep personal items and valuables away from the work area.",
      "Fan & Ceiling service requires safe and accessible working conditions; unsafe or high-level work is excluded.",
      "Furniture, bedroom and wardrobe work covers normal accessible cleaning/arrangement; heavy shifting or dismantling is excluded.",
      "Window and glass service is for cleaning accessible surfaces only; glass repair or replacement is excluded.",
      "Sand Paint / Rangoli / Floor Art is for the booked surface activity; permanent painting, cement removal or restoration is excluded.",
      "Please inform the partner about existing damage, fragile items or special conditions before service starts.",
      "Any heavy, technical, repair, specialized or additional work requires a separate service or customer approval.",
      "Booking Reminder: Please select the exact CN Pro service you need.",
    ],
  },
  {
    // Same bundled multi-room/duration mechanic as Instant CN Pro above —
    // the one difference is the professional: a verified, company-employed
    // staff member rather than an independent partner.
    id: "hs-cn-plus",
    name: "Instant CN Pro Plus",
    desc: "Same bundled multi-room cleaning as CN Pro, carried out by a verified, company-trusted professional.",
    ...HOME_SERVICES["hs-cn-plus"],
    image: complete_house_cleaning_img,
    images: [complete_house_cleaning_img],
    color: "#0ea5e9",
    isSpecial: true,
    // The one difference from CN Pro — a verified, company-employed
    // professional rather than a partner — gets its own badge alongside
    // the shared "Featured" ribbon, instead of only being mentioned in text.
    isVerified: true,
    includes: [
      "Verified, company-employed professional (not a partner)",
      "Kitchen Cleaning",
      "Utensil Cleaning",
      "Sweeping & Mopping",
      "Dusting & Wiping",
      "Kitchen Cabinet Cleaning",
      "Refrigerator Cleaning",
      "Microwave & Oven & Grill Cleaning",
      "Chimney Cleaning",
      "Display Cabinet / Showcase Cleaning",
      "Balcony & Entrance Cleaning",
      "Steps / Staircase Cleaning",
      "Grills / Steps / Parking Space Cleaning",
      "Packing & Unpacking",
      "Fish Tank Cleaning",
      "Sand Paint / Rangoli / Floor Art",
      "Window & Glass",
    ],
    excludes: [
      "Deep Restoration / Permanent Damage Repair",
      "Heavy Stains / Heavy Grease Restoration",
      "Heavy Furniture Shifting / Dismantling",
      "Appliance / Chimney Repair or Dismantling",
      "Plumbing / Electrical / Structural Work",
      "Heavy Utensil Cleaning / Complex Food Preparation",
      "Glass Repair / Replacement",
      "Carpentry / Cabinet Repair",
      "Packing Transportation / Heavy Moving",
      "Fish Medical Treatment / Pump or Filter Repair",
      "Permanent Painting / Specialized Surface Restoration",
      "High-Level, Unsafe or Unlisted Work",
    ],
    customerNotes: [
      "The selected CN Pro or CN Pro Plus service covers only the listed Included Service. Additional, repair, technical, heavy, unsafe or specialized work requires a separate service or customer approval.",
      "The selected Pro Plus service covers cleaning or assistance for the listed service only.",
      "Refrigerator, Microwave & Oven & Grill, and Chimney services are cleaning-focused; repair or dismantling is excluded.",
      "Display Cabinet / Showcase and Kitchen Cabinet services cover accessible cleaning; carpentry or cabinet repair is excluded.",
      "Balcony, Steps, Grills and Parking Space work is routine cleaning only; heavy restoration or structural work is excluded.",
      "Packing & Unpacking covers packing/unpacking assistance; transportation and heavy moving are excluded.",
      "Fish Tank Cleaning covers cleaning care only; medical treatment, pump repair or filter repair is excluded.",
      "Window & Glass service is for cleaning accessible glass surfaces; glass repair or replacement is excluded.",
      "Sand Paint / Rangoli / Floor Art is for the booked activity; permanent painting or specialized surface restoration is excluded.",
      "Please clear the work area, secure valuables and report fragile or pre-existing damage before service.",
      "High-level, unsafe, technical, repair or unlisted work may require a separate service or customer approval.",
      "Booking Reminder: Please select the exact Pro Plus service, keep the work area accessible and inform the partner in advance about fragile items, existing damage or special site conditions.",
    ],
  },
  {
    // Quick Services Access parity (QAS_IHCK) — converted from the old
    // room-based/subServices version to a duration-sold Instant booking,
    // same mechanic as Instant CN Pro (hs-11) just not bundled/featured.
    id: "hs-1",
    name: "Kitchen Cleaning",
    desc: "Deep cleaning of kitchen, slab, cabinets, sink & appliances.",
    ...HOME_SERVICES["hs-1"],
    image: qas_kitchen_cleaning_img,
    images: [qas_kitchen_cleaning_img],
    color: "#6d5efc",
    isInstant: true,
    includes: [
      "Kitchen Platform / Counter Cleaning",
      "Stove & Hob Surface Cleaning",
      "Sink & Faucet Cleaning",
      "Kitchen Floor Sweeping & Mopping",
      "Backsplash / Accessible Wall Cleaning",
      "Cabinet Exterior Cleaning",
      "Appliance Exterior Wiping",
      "Basic Grease & Food-Residue Removal",
    ],
    excludes: [
      "Utensil Cleaning",
      "Cabinet Deep Interior Cleaning Unless Booked",
      "Chimney Dismantling / Motor Cleaning",
      "Appliance Repair",
      "Heavy Construction / Cement Removal",
      "Pest Control",
      "Plumbing / Electrical Repair",
      "Heavy Furniture / Appliance Shifting",
    ],
    customerNotes: "Keep counters accessible, remove valuables/fragile items, secure food and personal items, inform the professional about existing stains/damage, and keep children/pets away.",
    // Optional extras shown on the Instant detail page's right column (in
    // place of the hero image) — real catalog service ids, not free text,
    // so each add-on's own name/image/price is read straight from its own
    // entry in this array (single source of truth, no separate lookup to
    // keep in sync).
    addOns: ["hs-utensil", "hs-kitchen-cabinet", "hs-fridge", "hs-microwave-oven", "hs-sweeping-mopping"],
  },
  {
    // Converted from the old room-based/subServices + standalone
    // /service/home-service/bathroom-cleaning page to a duration-sold
    // Instant booking — same conversion already done for Kitchen Cleaning,
    // Laundry & Ironing, Window and Glass Cleaning, Packing & Unpacking.
    // Now lives at /service/home-service/instant/hs-2 instead.
    id: "hs-2",
    name: "Bathroom Cleaning",
    desc: "Toilet, washbasin, tiles, fittings and floor deep cleaning.",
    ...HOME_SERVICES["hs-2"],
    image: qas_bathroom_img,
    images: [qas_bathroom_img],
    color: "#2dd4bf",
    isInstant: true,
    includes: [
      "Toilet Cleaning",
      "Washbasin Cleaning",
      "Faucet / Tap Cleaning",
      "Floor Cleaning",
      "Tile Surface Cleaning",
      "Visible Fixture Cleaning",
      "Bathroom Mopping",
      "Basic Deodorizing",
    ],
    excludes: [
      "Heavy Mold Restoration",
      "Severe Hard-Water Restoration",
      "Specialist Grout Restoration",
      "Deep Drain / Sewage Work",
      "Plumbing Repair",
      "Waterproofing",
      "Heavy Construction Stain Removal",
    ],
    customerNotes: "Remove personal toiletries where possible, keep water access available, disclose fragile fixtures, and keep children/pets away during chemical use.",
    addOns: ["hs-sweeping-mopping", "hs-dusting-wiping", "hs-9", "hs-outside-spaces", "hs-furniture"],
  },
  {
    // Converted from the old room-based/subServices + standalone
    // /service/home-service/balcony-cleaning page to a duration-sold
    // Instant booking — same conversion already done for Kitchen Cleaning,
    // Bathroom Cleaning, Laundry & Ironing, Window and Glass Cleaning,
    // Packing & Unpacking. Now lives at
    // /service/home-service/instant/hs-4 instead.
    id: "hs-4",
    name: "Balcony Cleaning",
    desc: "Sweeping, mopping and railing deep cleaning.",
    ...HOME_SERVICES["hs-4"],
    image: qas_balcony_img,
    images: [qas_balcony_img, livingroom_balcony_img],
    color: "#ec4899",
    isInstant: true,
    includes: [
      "Balcony Sweeping & Mopping",
      "Entrance Floor Cleaning",
      "Door / Entrance Wiping",
      "Accessible Grill Cleaning",
      "Basic Dust / Cobweb Removal",
      "Basic Arrangement",
    ],
    excludes: [
      "External Building Façade",
      "High-Level Exterior Cleaning",
      "Gardening",
      "Pest Control",
      "Drain Repair",
      "Waterproofing",
      "Heavy Construction Debris",
    ],
    customerNotes: "Remove plants/objects requiring protection, secure valuables, and ensure balcony access is safe.",
  },
  {
    // Quick Services Access parity (QAS_IHWG) — duration-sold Instant.
    id: "hs-9",
    name: "Window and Glass Cleaning",
    desc: "Glass, window frame, tracks and mesh cleaning.",
    ...HOME_SERVICES["hs-9"],
    image: qas_window_glass_img,
    images: [qas_window_glass_img],
    color: "#2dd4bf",
    isInstant: true,
    includes: [
      "Window Glass Cleaning",
      "Window Frame Cleaning",
      "Sill Cleaning",
      "Accessible Grills",
      "Mirror / Glass Surface Cleaning",
      "Door Surface Wiping",
      "Door Frame Cleaning",
      "Handles",
      "Accessible Wall Spot Cleaning",
    ],
    excludes: [
      "High-Level Exterior Windows",
      "Glass Repair / Replacement",
      "Paint / Cement Restoration",
      "Permanent Stain Removal",
      "Wall Painting",
      "Structural Repair",
      "Unsafe Access",
    ],
    customerNotes: "Windows must be safely accessible; disclose cracked glass or damaged frames; high/exterior areas may need separate equipment.",
    addOns: ["hs-dusting-wiping", "hs-furniture", "hs-sweeping-mopping", "hs-outside-spaces", "hs-showcase"],
  },
  {
    // Quick Services Access parity (QAS_IHPU) — duration-sold Instant.
    id: "hs-10",
    name: "Packing & Unpacking",
    desc: "Careful packing and unpacking support for your belongings.",
    ...HOME_SERVICES["hs-10"],
    image: qas_packing_unpacking_img,
    images: [qas_packing_unpacking_img],
    color: "#f97316",
    isInstant: true,
    includes: [
      "Household Item Packing",
      "Unpacking",
      "Basic Sorting",
      "Labeling",
      "Basic Box Arrangement",
      "Clothes / Household Item Folding",
      "Basic Move Preparation Support",
    ],
    excludes: [
      "Heavy Furniture Shifting",
      "Transportation",
      "Moving-Truck Loading / Unloading",
      "Specialized Fragile-Item Packaging",
      "Valuation / Insurance of Items",
      "Appliance Disconnection",
      "Electrical / Plumbing Work",
    ],
    customerNotes: "Customer should provide boxes/packing materials unless included; valuables, jewellery, documents and fragile items should be secured by the customer.",
    addOns: ["hs-wardrobe", "hs-bedroom-arrangement", "hs-furniture", "hs-dusting-wiping"],
  },

  // ── Quick Services Access parity — the remaining QAS_* services with no
  // existing room-based category, added as duration-sold Instant bookings
  // (same mechanic as Instant CN Pro/Plus above, just not bundled/featured
  // — see isInstant handling in the component below).
  {
    id: "hs-rangoli",
    name: "Rangoli Art",
    desc: "Traditional rangoli/kolam design drawn at your entrance or puja space.",
    ...HOME_SERVICES["hs-rangoli"],
    image: qas_rangoli_img,
    images: [qas_rangoli_img],
    color: "#f472b6",
    isInstant: true,
    includes: [
      "Basic Rangoli",
      "Simple Floor Art",
      "Sand / Powder Design Assistance",
      "Festival / Event Floor Decoration",
      "Customer-Provided Design Following",
      "Basic Cleanup of Loose Material",
    ],
    excludes: [
      "Permanent Floor Painting",
      "Wall Painting",
      "Chemical / Epoxy Art",
      "Structural Surface Repair",
      "Professional Mural Work",
      "Material Purchase Unless Added",
    ],
    customerNotes: "Customer should approve the design and location, provide special colors/materials if required, and allow sufficient drying time.",
    addOns: ["hs-sweeping-mopping", "hs-dusting-wiping", "hs-outside-spaces", "hs-furniture", "hs-9"],
  },
  {
    id: "hs-fish-tank",
    name: "Fish Tank Cleaning",
    desc: "Water change, gravel and glass cleaning for your aquarium.",
    ...HOME_SERVICES["hs-fish-tank"],
    image: qas_fish_tank_img,
    images: [qas_fish_tank_img],
    color: "#0ea5e9",
    isInstant: true,
    includes: [
      "Tank Glass Cleaning",
      "Algae / Surface Cleaning",
      "Partial Water Change",
      "Gravel / Base Cleaning",
      "Basic Filter Cleaning",
      "Basic Tank Interior Cleaning",
      "Refill Assistance",
      "Basic Area Cleanup",
    ],
    excludes: [
      "Fish Medical Treatment",
      "Fish Disease Diagnosis",
      "Fish Relocation Without Customer Instructions",
      "Aquarium Glass Repair / Replacement",
      "Pump / Filter Repair",
      "Major Electrical Repair",
      "Full Tank Relocation",
      "Chemical Treatment",
      "Water Quality Laboratory Testing",
    ],
    customerNotes: "Customer should identify fish species and special care instructions, provide suitable replacement water where required, disclose sensitive fish/plants, and keep electrical equipment away from water.",
    addOns: ["hs-dusting-wiping", "hs-furniture", "hs-9", "hs-sweeping-mopping", "hs-outside-spaces"],
  },
  {
    id: "hs-showcase",
    name: "Showcase Cleaning",
    desc: "Dusting and wipe-down of display cabinets, shelves and decor items.",
    ...HOME_SERVICES["hs-showcase"],
    image: qas_showcase_img,
    images: [qas_showcase_img],
    color: "#8b5cf6",
    isInstant: true,
    includes: [
      "Glass Cleaning",
      "Shelf Cleaning",
      "Exterior Dusting",
      "Interior Surface Wiping",
      "Frame Cleaning",
      "Handle Cleaning",
      "Basic Arrangement Assistance",
    ],
    excludes: [
      "Glass Replacement",
      "Glass Repair",
      "Carpentry",
      "Cabinet Repair",
      "Heavy Item Shifting",
      "Fragile Antique Restoration",
      "High-Level Unsafe Access",
    ],
    customerNotes: "Customer should remove highly valuable/fragile items where possible and identify antiques, collectibles or delicate glass before service.",
    addOns: ["hs-dusting-wiping", "hs-furniture", "hs-9", "hs-bedroom-arrangement", "hs-sweeping-mopping"],
  },
  {
    id: "hs-chimney",
    name: "Chimney Cleaning",
    desc: "Exterior and filter cleaning for your kitchen chimney.",
    ...HOME_SERVICES["hs-chimney"],
    image: qas_chimney_img,
    images: [qas_chimney_img],
    color: "#64748b",
    isInstant: true,
    includes: [
      "Chimney Exterior Cleaning",
      "Mesh Cleaning",
      "Filter Cleaning",
      "Steam Cleaning",
      "Grease / Oil Cleaning",
      "Accessible Surface Cleaning",
    ],
    excludes: [
      "Motor Cleaning",
      "Motor Repair",
      "Duct Cleaning Unless Separately Booked",
      "Chimney Dismantling",
      "Electrical Repair",
      "Automatic Chimney Service",
      "New Installation",
      "Structural Work",
    ],
    customerNotes: "Switch off the chimney before work, clear the stove area, disclose existing faults, and do not use the appliance until any wet cleaning has dried.",
    addOns: ["hs-1", "hs-kitchen-cabinet", "hs-utensil", "hs-microwave-oven", "hs-fridge"],
  },
  {
    id: "hs-microwave-oven",
    name: "Microwave & Oven Cleaning",
    desc: "Interior and exterior cleaning for your microwave and oven.",
    ...HOME_SERVICES["hs-microwave-oven"],
    image: qas_microwave_oven_img,
    images: [qas_microwave_oven_img],
    color: "#eab308",
    isInstant: true,
    includes: [
      "Microwave Exterior Cleaning",
      "Microwave Interior Wiping",
      "Oven Interior Cleaning",
      "Oven Exterior Cleaning",
      "Tray / Rack Cleaning",
      "Food Spill Removal",
      "Basic Grease / Odour Removal",
      "Grill Plate / Surface Cleaning",
    ],
    excludes: [
      "Appliance Repair",
      "Electrical Fault Repair",
      "PCB Repair",
      "Heating Element / Magnetron Work",
      "Gas Connection Work",
      "Dismantling",
      "Heavy Component Replacement",
    ],
    customerNotes: "Switch off and cool the appliance before cleaning; remove food/items; disclose damaged electrical cords, glass or trays.",
    addOns: ["hs-1", "hs-utensil", "hs-kitchen-cabinet", "hs-fridge", "hs-food-prep"],
  },
  {
    id: "hs-kitchen-cabinet",
    name: "Kitchen Cabinet Cleaning",
    desc: "Wipe-down and tidy-up of kitchen cabinets, shelves and drawers.",
    ...HOME_SERVICES["hs-kitchen-cabinet"],
    image: qas_kitchen_cabinet_img,
    images: [qas_kitchen_cabinet_img],
    color: "#a855f7",
    isInstant: true,
    includes: [
      "Cabinet Exterior Cleaning",
      "Accessible Cabinet Interior Cleaning",
      "Shelf Cleaning",
      "Trolley Cleaning",
      "Grease & Oil Removal",
      "Handle Cleaning",
      "Cabinet Bottom / Edge Cleaning",
    ],
    excludes: [
      "Cabinet Repair",
      "Carpenter Work",
      "Laminate Replacement",
      "Hardware Replacement",
      "Major Water-Damaged Wood Restoration",
      "Pest / Termite Treatment",
      "Heavy Furniture Shifting",
    ],
    customerNotes: "Customer should remove valuables/fragile items and food items where necessary; disclose damaged/loose shelves before cleaning.",
    addOns: ["hs-1", "hs-utensil", "hs-fridge", "hs-microwave-oven", "hs-food-prep"],
  },
  {
    id: "hs-wardrobe",
    name: "Wardrobe Arrangement",
    desc: "Folding, sorting and arranging clothes and items in your wardrobe.",
    ...HOME_SERVICES["hs-wardrobe"],
    image: qas_wardrobe_img,
    images: [qas_wardrobe_img],
    color: "#14b8a6",
    isInstant: true,
    includes: [
      "Wardrobe Surface Dusting",
      "Accessible Interior Cleaning",
      "Clothes Folding",
      "Clothes Arrangement",
      "Basic Shelf Arrangement",
      "Basic Categorization",
      "Existing Storage Reorganization",
    ],
    excludes: [
      "Heavy Furniture Shifting",
      "Wardrobe Dismantling",
      "Pest Treatment",
      "Mold Restoration",
      "Carpenter Repair",
      "Full Property Decluttering Outside the Wardrobe",
      "Disposal of Valuable/Personal Items",
    ],
    customerNotes: "Customer should decide what to retain/discard; professionals should not dispose of belongings without permission; valuables and documents should be secured.",
    addOns: ["hs-bedroom-arrangement", "hs-10", "hs-dusting-wiping", "hs-furniture"],
  },
  {
    id: "hs-bedroom-arrangement",
    name: "Bedroom Arrangement",
    desc: "Tidying and arranging bedroom furniture, linen and décor.",
    ...HOME_SERVICES["hs-bedroom-arrangement"],
    image: qas_bedroom_arrange_img,
    images: [qas_bedroom_arrange_img],
    color: "#fb7185",
    isInstant: true,
    includes: [], excludes: [],
  },
  {
    id: "hs-outside-spaces",
    name: "Outside Spaces Cleaning",
    desc: "Sweeping and tidying of entrance, doorstep and parking areas.",
    ...HOME_SERVICES["hs-outside-spaces"],
    image: qas_outside_spaces_img,
    images: [qas_outside_spaces_img],
    color: "#65a30d",
    isInstant: true,
    includes: [
      "Balcony Sweeping & Mopping",
      "Entrance Floor Cleaning",
      "Door / Entrance Wiping",
      "Accessible Grill Cleaning",
      "Basic Dust / Cobweb Removal",
      "Basic Arrangement",
    ],
    excludes: [
      "External Building Façade",
      "High-Level Exterior Cleaning",
      "Gardening",
      "Pest Control",
      "Drain Repair",
      "Waterproofing",
      "Heavy Construction Debris",
    ],
    customerNotes: "Remove plants/objects requiring protection, secure valuables, and ensure balcony access is safe.",
    addOns: ["hs-sweeping-mopping", "hs-dusting-wiping", "hs-9", "hs-furniture", "hs-steps-grills-parking"],
  },
  {
    id: "hs-fan-switchboard-ceiling",
    name: "Fan & Ceiling Cleaning",
    desc: "Dusting and wipe-down of ceiling fans, switchboards and ceiling corners.",
    ...HOME_SERVICES["hs-fan-switchboard-ceiling"],
    image: qas_fan_switchboard_img,
    images: [qas_fan_switchboard_img],
    color: "#38bdf8",
    isInstant: true,
    includes: [
      "Accessible Ceiling Fan Dusting",
      "Fan Blade Cleaning",
      "Fan Exterior Cleaning",
      "Accessible Ceiling Dusting",
      "Cobweb Removal",
      "Fan Surface Wiping",
    ],
    excludes: [
      "Fan Repair",
      "Fan Dismantling",
      "Motor Cleaning / Servicing",
      "High-Level Work Without Safe Access",
      "Ceiling Repair",
      "Electrical Work",
    ],
    customerNotes: "Power may need to be switched off; customer must provide a safe ladder where required because regular instant cleaning should not involve unsafe height work.",
    addOns: ["hs-dusting-wiping", "hs-sweeping-mopping", "hs-9", "hs-furniture", "hs-showcase"],
  },
  {
    id: "hs-furniture",
    name: "Furniture Cleaning",
    desc: "Dusting, wiping and stain treatment for chairs, tables and furniture.",
    ...HOME_SERVICES["hs-furniture"],
    image: qas_furniture_img,
    images: [qas_furniture_img],
    color: "#22c55e",
    isInstant: true,
    includes: [
      "Dusting",
      "Wiping",
      "Tables & Chairs",
      "Cabinets / Shelves",
      "Accessible Furniture Surfaces",
      "Basic Spot Cleaning",
    ],
    excludes: [
      "Heavy Furniture Shifting",
      "Dismantling",
      "Upholstery Shampooing Unless Booked",
      "Wood Restoration",
      "Polishing / Repainting",
      "Pest / Termite Treatment",
    ],
    customerNotes: "Remove valuables and fragile items, identify delicate wood/finish, and approve movement of furniture before work.",
    // "Bedroom & Wardrobe Arrangement" in the given list isn't a single
    // catalog entry — it covers both hs-bedroom-arrangement and hs-wardrobe,
    // so both are included here rather than picking one.
    addOns: ["hs-dusting-wiping", "hs-sweeping-mopping", "hs-showcase", "hs-9", "hs-bedroom-arrangement", "hs-wardrobe"],
  },
  {
    id: "hs-dusting-wiping",
    name: "Dusting & Wiping",
    desc: "General dusting and wiping of surfaces across your home.",
    ...HOME_SERVICES["hs-dusting-wiping"],
    image: qas_dusting_wiping_img,
    images: [qas_dusting_wiping_img],
    color: "#60a5fa",
    isInstant: true,
    includes: [
      "Shelves",
      "Furniture",
      "Tables & Counters",
      "Window Sills",
      "Accessible Grills",
      "Decor Items",
      "Appliance Exteriors",
      "Accessible Cobweb Removal",
    ],
    excludes: [
      "High / Unsafe Areas",
      "Ladder Work",
      "Internal Electronics",
      "Heavy Stain Restoration",
      "Specialized Equipment Cleaning",
      "Heavy Furniture Shifting",
    ],
    customerNotes: "Secure valuables and electronics, identify fragile decor, keep surfaces accessible, and tell the professional about delicate finishes.",
    addOns: ["hs-sweeping-mopping", "hs-furniture", "hs-9", "hs-fan-switchboard-ceiling", "hs-showcase"],
  },
  {
    id: "hs-sweeping-mopping",
    name: "Sweeping & Mopping",
    desc: "Floor sweeping and mopping across your home.",
    ...HOME_SERVICES["hs-sweeping-mopping"],
    image: qas_sweeping_mopping_img,
    images: [qas_sweeping_mopping_img],
    color: "#fbbf24",
    isInstant: true,
    includes: [
      "Sweeping Accessible Floors",
      "Dry Dust Removal",
      "Wet Mopping",
      "Corners & Edges",
      "Basic Floor Spot Cleaning",
      "Balcony Floor Sweeping/Mopping",
    ],
    excludes: [
      "Heavy Stain Restoration",
      "Carpet Shampooing",
      "Floor Polishing",
      "Heavy Furniture Shifting",
      "High-Level Cleaning",
      "Outdoor/Public-Area Cleaning",
      "Construction Debris Removal",
    ],
    customerNotes: "Remove small movable objects where possible, keep floors accessible, secure fragile items, and disclose slippery/damaged flooring.",
    addOns: ["hs-dusting-wiping", "hs-2", "hs-outside-spaces", "hs-furniture", "hs-9"],
  },
  {
    id: "hs-food-prep",
    name: "Food Preparation",
    desc: "Washing, cutting and prepping vegetables and ingredients.",
    ...HOME_SERVICES["hs-food-prep"],
    image: qas_food_prep_img,
    images: [qas_food_prep_img],
    color: "#f97316",
    isInstant: true,
    includes: [
      "Vegetable Washing & Cutting",
      "Fruit Peeling / Chopping",
      "Ingredient Sorting",
      "Basic Grinding / Mixing",
      "Dough / Batter Preparation",
      "Basic Rice / Dal Preparation",
      "Measuring & Mixing",
      "Basic Cooking Assistance",
    ],
    excludes: [
      "Full Meal Cooking",
      "Complex / Specialized Cooking",
      "Professional Baking",
      "Heavy Meat / Fish / Poultry Processing",
      "Butchering",
      "Grocery Shopping",
      "Food Delivery",
      "Appliance Repair",
    ],
    customerNotes: "Provide ingredients and clean work area; specify recipe/instructions; disclose allergies or food restrictions; customer remains responsible for ingredient quality and food safety.",
    addOns: ["hs-utensil", "hs-1", "hs-microwave-oven", "hs-fridge", "hs-kitchen-cabinet"],
  },
  {
    id: "hs-utensil",
    name: "Utensil Cleaning",
    desc: "Washing of kitchen utensils, plates, bowls and cutlery.",
    ...HOME_SERVICES["hs-utensil"],
    image: qas_utensil_img,
    images: [qas_utensil_img],
    color: "#2dd4bf",
    isInstant: true,
    includes: [
      "Daily Utensil Washing",
      "Scrubbing & Rinsing",
      "Drying",
      "Basic Arrangement",
      "Sink Cleaning After Utensils",
    ],
    excludes: [
      "Heavy / Extra-Large Vessels",
      "Burnt Utensils Requiring Heavy Restoration",
      "Heavy Grease Buildup",
      "Broken / Sharp-Damaged Utensils",
      "Kitchen Deep Cleaning",
      "Appliance Cleaning",
    ],
    customerNotes: "Separate sharp/fragile items, identify valuable cookware, provide safe water/sink access, and tell the professional about items requiring special care.",
    addOns: ["hs-1", "hs-food-prep", "hs-kitchen-cabinet", "hs-fridge", "hs-microwave-oven"],
  },
  {
    id: "hs-fridge",
    name: "Fridge Cleaning",
    desc: "Interior and exterior cleaning for your refrigerator.",
    ...HOME_SERVICES["hs-fridge"],
    image: qas_fridge_img,
    images: [qas_fridge_img],
    color: "#4ade80",
    isInstant: true,
    includes: [
      "Refrigerator Exterior Cleaning",
      "Interior Shelf / Compartment Cleaning",
      "Food Spill Cleaning",
      "Odour Cleaning",
      "Door / Gasket Wiping",
      "Tray / Rack Cleaning",
      "Basic Drying & Arrangement",
    ],
    excludes: [
      "Refrigerator Repair",
      "Gas / Refrigerant Work",
      "Compressor Work",
      "Electrical Repair",
      "PCB Repair",
      "Major Ice Removal Requiring Service",
      "Disposal of Expired / Spoiled Food Unless Customer Removes It",
    ],
    customerNotes: "Customer should remove food and valuables beforehand, keep the refrigerator accessible, and inform the professional about fragile shelves, leaks or existing damage.",
    addOns: ["hs-1", "hs-utensil", "hs-kitchen-cabinet", "hs-food-prep", "hs-microwave-oven"],
  },
  {
    // New catalog entry — referenced as an add-on by several other
    // services (Balcony & Entrance Cleaning, Sand Paint/Rangoli/Floor Art,
    // itself) but had no standalone page yet. No QAS_* asset exists for
    // this one, so it reuses Outside Spaces Cleaning's photo (closest
    // thematic match: entrance/steps/parking are all exterior spaces) —
    // includes/excludes/customerNotes still need real content.
    id: "hs-steps-grills-parking",
    name: "Steps & Parking Space Cleaning",
    desc: "Sweeping and cleaning of steps and parking space areas.",
    ...HOME_SERVICES["hs-steps-grills-parking"],
    image: qas_outside_spaces_img,
    images: [qas_outside_spaces_img],
    color: "#65a30d",
    isInstant: true,
    includes: [],
    excludes: [],
    addOns: ["hs-sweeping-mopping", "hs-dusting-wiping", "hs-outside-spaces", "hs-9", "hs-rangoli"],
  },
];

const extraServices = [
  { name: "Baby Caretaker",          link: "/service/caretaker-service/baby-care",       image: baby_caretaker_img },
  { name: "Medical Care Representative", link: "/service/caretaker-service/medical-care-representative", image: medical_caretaker_img },
  { name: "Deep Cleaning",           link: "/service/special-service/deep-cleaning",         image: deep_cleaning_img },
  { name: "Kitchen & Utensil Cleaning", link: "/service/special-service/kitchen-utility-cleaning", image: home_utensils_cleaning_img },
  { name: "Car Washing",             link: "/services/outdoor-services/carwash",      image: vehicle_washing_extra_img },
  { name: "Gardening Services",      link: "/services/outdoor-services/gardening",     image: gardening_extra_img },
];

// ─── Details Modal ────────────────────────────────────────────────────────────
const DetailsModal = ({ service, onClose, onAddToCart, cartQty }) => {
  const [slide, setSlide] = useState(0);
  const { cityPrice } = useCityPrice();
  const navigate = useNavigate();
  if (!service) return null;

  // Duration-sold (Instant CN Pro/Plus + every Quick Services Access
  // parity service) books from its own dedicated page, not inline here.
  const durationSold = service.isSpecial || service.isInstant;

  const images = Array.isArray(service.images) && service.images.length
    ? service.images
    : [service.image].filter(Boolean);

  const prev = () => setSlide((s) => (s - 1 + images.length) % images.length);
  const next = () => setSlide((s) => (s + 1) % images.length);

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={M.header}>
          <div>
            <h2 style={M.title}>{service.name}</h2>
            <div style={M.meta}>
              <span style={M.price}>₹{cityPrice(service.id, service.price)}</span>
              <span style={M.dot}>·</span>
              <span style={M.dur}>{service.duration}</span>
            </div>
          </div>
          <button style={M.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Image slider */}
        <div style={M.sliderWrap}>
          <img
            src={images[slide]}
            alt={`${service.name} ${slide + 1}`}
            style={M.sliderImg}
            onError={(e) => { e.currentTarget.src = fallbackImage; }}
          />
          {images.length > 1 && (
            <>
              <button style={{ ...M.arrow, left: 10 }} onClick={prev}>‹</button>
              <button style={{ ...M.arrow, right: 10 }} onClick={next}>›</button>
              <div style={M.dots}>
                {images.map((_, i) => (
                  <button
                    key={i}
                    style={{ ...M.dot2, ...(i === slide ? M.dotActive : {}) }}
                    onClick={() => setSlide(i)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Description */}
        {service.desc && <p style={M.desc}>{service.desc}</p>}

        {/* Sub-services chips */}
        {Array.isArray(service.subServices) && service.subServices.filter((sub) => isServiceEnabled(sub.id)).length > 0 && (
          <div style={M.subServicesWrap}>
            <div style={M.subServicesLabel}>Included Tasks</div>
            <div style={M.subServicesRow}>
              {service.subServices.filter((sub) => isServiceEnabled(sub.id)).map((sub) => (
                <span key={sub.id} style={M.subServiceChip}>
                  {sub.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Do / Don't grid */}
        <div style={M.grid}>
          {/* What we do */}
          <div style={M.panel}>
            <div style={M.panelHead}>
              <span style={{ ...M.panelBadge, background: "#ecfdf5", color: "#15803d" }}>✓</span>
              <h4 style={M.panelTitle}>What we do</h4>
            </div>
            <ul style={M.list}>
              {(service.includes || []).map((item) => (
                <li key={item} style={M.listItem}>
                  <span style={M.listIcon}>✔</span>
                  <span>{item}</span>
                </li>
              ))}
              {(!service.includes || service.includes.length === 0) && (
                <li style={{ ...M.listItem, color: "#94a3b8" }}>No details available.</li>
              )}
            </ul>
          </div>

          {/* What we won't do */}
          <div style={M.panel}>
            <div style={M.panelHead}>
              <span style={{ ...M.panelBadge, background: "#fff1f2", color: "#dc2626" }}>✕</span>
              <h4 style={M.panelTitle}>What we won't do</h4>
            </div>
            <ul style={M.list}>
              {(service.excludes || []).map((item) => (
                <li key={item} style={M.listItem}>
                  <span style={{ ...M.listIcon, color: "#ef4444" }}>✖</span>
                  <span>{item}</span>
                </li>
              ))}
              {(!service.excludes || service.excludes.length === 0) && (
                <li style={{ ...M.listItem, color: "#94a3b8" }}>No details available.</li>
              )}
            </ul>
          </div>
        </div>

        {/* Customer Notes — either a single sentence (string) or a
            bulleted list (array), e.g. CN Pro's longer scope notes. */}
        {service.customerNotes && (
          <div style={M.notesBox}>
            <div style={M.notesLabel}>📝 Customer Notes</div>
            {Array.isArray(service.customerNotes) ? (
              <ul style={M.notesList}>
                {service.customerNotes.map((note) => (
                  <li key={note} style={M.notesListItem}>{note}</li>
                ))}
              </ul>
            ) : (
              <p style={M.notesText}>{service.customerNotes}</p>
            )}
          </div>
        )}

        {/* Footer CTA */}
        {durationSold ? (
          <div style={M.footer}>
            <button
              style={M.addBtn}
              onClick={() => { onClose(); navigate(`/service/home-service/instant/${service.id}`); }}
            >
              Book Now
            </button>
          </div>
        ) : !service.link ? (
          <div style={M.footer}>
            {cartQty > 0 ? (
              <div style={M.qtyRow}>
                <span style={M.inCartLabel}>✓ In Cart</span>
                <span style={M.qtyBadge}>{cartQty} × ₹{cityPrice(service.id, service.price)} = ₹{cartQty * cityPrice(service.id, service.price)}</span>
              </div>
            ) : null}
            <button
              style={{ ...M.addBtn, ...(cartQty > 0 ? M.addBtnGreen : {}) }}
              onClick={() => { onAddToCart(service); onClose(); }}
            >
              {cartQty > 0 ? "✓ Add Another" : "+ Book"}
            </button>
          </div>
        ) : (
          <div style={M.footer}>
            <button style={M.addBtn} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const HomeServices = () => {
  const navigate = useNavigate();
  const { addItem, isInCart, getItem } = useCart();
  const { cityPrice } = useCityPrice();
  const [modalService, setModalService] = useState(null);

  // Remote config (service enable/disable + region pricing) is fetched once
  // on app load, but if this page is the first one visited this session it
  // may not have resolved yet — re-fetch (no-op if already done) and force
  // one re-render once it lands, so isServiceEnabled()/cityPrice() reads
  // (both plain, unmemoized calls made fresh on every render) reflect the
  // latest Dashboard settings instead of whatever was cached at module load.
  const [, forceConfigRerender] = useState(0);
  useEffect(() => {
    fetchRemoteConfig().then(() => forceConfigRerender((n) => n + 1));
  }, []);

  // Plain, unmemoized filter (same reasoning as isServiceEnabled()/
  // cityPrice() above) — recomputed every render so it always reflects
  // the latest Dashboard Enable/Disable setting.
  const visibleServices = services.filter((service) => isServiceEnabled(service.id));

  // Duration-sold services (Instant CN Pro/Plus + Quick Services Access
  // parity) no longer book from this page — "Book Now" navigates to
  // InstantServiceDetailPage.js, which owns the duration stepper, price
  // summary and the actual add-to-cart + navigate-to-checkout logic that
  // used to live here as handleBookInstantSpecial.

  const handleBookNow = (service) => {
    if (service.link) {
      navigate(service.link);
    } else {
      addItem({
        id:       service.id,
        title:    service.name,
        name:     service.name,
        subtitle: service.duration,
        price:    cityPrice(service.id, service.price),
        image:    typeof service.image === "string" ? service.image : "",
        category: "Home Services",
      });
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.layout}>

        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <CategorySidebar />
          <div style={styles.helpCard}>
            <h4 style={styles.helpTitle}>Need Help Choosing the Right Service?</h4>
            <p style={styles.helpText}>Our experts are here to help you 24/7</p>
            <Link to="/contact-us" style={{ ...styles.chatBtn, textDecoration: "none", display: "inline-block" }}>💬 Chat with Us</Link>
            <div style={styles.helpIcon}>🎧</div>
          </div>
        </aside>

        {/* Main */}
        <main style={styles.main}>
          <div style={styles.breadcrumb}>Home &gt; Home Services</div>

          {/* Section header */}
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Popular Home Services</h2>
            <span style={styles.serviceCount}>Showing {visibleServices.length} services</span>
          </div>

          {/* Service cards — a service the Dashboard's Enable/Disable tab
              turns off is left out of this list entirely, not shown
              disabled, so disabling one is indistinguishable from it never
              having existed on this page. */}
          <div style={styles.serviceGrid}>
            {visibleServices.map((service) => {
              const inCart  = isInCart(service.id);
              const cartQty = getItem(service.id)?.qty || 0;
              // Duration-sold like Instant CN Pro/Plus — these skip the
              // inline duration stepper + add-to-cart entirely; "Book Now"
              // instead opens a dedicated page (InstantServiceDetailPage)
              // where duration, price summary and basic details live.
              const durationSold = service.isSpecial || service.isInstant;

              return (
                <div
                  key={service.id}
                  style={{
                    ...styles.serviceCard,
                    ...(service.isSpecial ? styles.specialCard : {}),
                  }}
                >
                  <img
                    src={service.image}
                    alt={service.name}
                    style={styles.serviceImage}
                    onError={(e) => { e.currentTarget.src = fallbackImage; }}
                  />

                  {/* Instant service badge — highlighted ribbon for the
                      featured bundled options (CN Pro / CN Plus). Generic
                      "Featured" text, not tied to either service's name. */}
                  <div style={service.isSpecial ? styles.specialRibbon : styles.instantBadge}>
                    {service.isSpecial ? "⭐ Featured" : "⚡ Instant"}
                  </div>

                  {/* Verified badge — CN Plus only, alongside Featured;
                      its one difference from CN Pro (a verified,
                      company-employed professional) called out visually,
                      not just in the description text. */}
                  {service.isVerified && (
                    <div style={styles.verifiedBadge}>✅ Verified</div>
                  )}

                  {/* Qty badge if in cart — only meaningful for the
                      room-based services that still add-to-cart inline;
                      duration-sold ones book from the detail page instead. */}
                  {inCart && !durationSold && (
                    <div style={styles.qtyBadge}>{cartQty} in cart</div>
                  )}

                  <div style={styles.serviceBody}>
                    <div style={{ ...styles.serviceIcon, background: `${service.color}20`, color: service.color }}>
                      ✦
                    </div>
                    <h3 style={styles.serviceName}>{service.name}</h3>
                    <p style={styles.serviceDesc}>{service.desc}</p>

                    {/* Price only — no duration/hours label. The actual
                        duration for duration-sold services is picked on
                        the next page, not estimated here; room-based
                        services' price already reflects the Dashboard's
                        config via cityPrice(). */}
                    <div style={styles.serviceMeta}>
                      <span style={styles.price}>₹{cityPrice(service.id, service.price)}</span>
                    </div>

                    {/* Buttons row */}
                    <div style={styles.btnRow}>
                      <button
                        style={{
                          ...styles.bookBtn,
                          ...(service.isSpecial ? styles.specialBookBtn : {}),
                          ...(inCart && !service.link && !durationSold ? styles.bookBtnAdded : {}),
                        }}
                        onClick={() => {
                          if (durationSold) { navigate(`/service/home-service/instant/${service.id}`); return; }
                          handleBookNow(service);
                        }}
                      >
                        {durationSold
                          ? "Book Now"
                          : service.link
                          ? "Book Now"
                          : inCart
                          ? `✓ Added (${cartQty})`
                          : "Book"}
                      </button>

                      <button
                        style={styles.detailsBtn}
                        onClick={() => setModalService(service)}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* See our professionals at work */}
          <h3 style={styles.gallerySectionTitle}>See our professionals at work</h3>
          <div style={styles.galleryGrid}>
            <div style={styles.galleryCard}>
              <img src={complete_house_cleaning_img} alt="Complete home cleaning" style={styles.galleryImage}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
              <div style={styles.galleryCaption}>Complete home cleaning</div>
            </div>
            <div style={styles.galleryCard}>
              <img src={bathroom_cleaning_img} alt="Bathroom deep cleaning" style={styles.galleryImage}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
              <div style={styles.galleryCaption}>Bathroom deep cleaning</div>
            </div>
          </div>

          {/* Banner */}
          <div style={styles.bannerCard}>
            <img
              src={deep_cleaning_img}
              alt="deep cleaning"
              style={styles.bannerImage}
              onError={(e) => { e.currentTarget.src = fallbackImage; }}
            />
            <div style={styles.bannerContent}>
              <h3 style={styles.bannerTitle}>Whole Home Deep Cleaning</h3>
              <p style={styles.bannerText}>Complete deep cleaning of your entire home</p>
              <div style={styles.bannerTags}>
                <span>All Rooms</span><span>Kitchen</span><span>Bathrooms</span><span>Balcony</span>
              </div>
            </div>
            <div style={styles.bannerAction}>
              <p style={styles.saveText}>Save from ₹1499</p>
              <button
                style={styles.bookNowBtn}
                onClick={() => navigate("/service/special-service/deep-cleaning")}
              >
                Book Now →
              </button>
            </div>
          </div>

          {/* Explore */}
          <div style={styles.exploreSection}>
            <h3 style={styles.exploreTitle}>Explore Other Services</h3>
            <div style={styles.exploreGrid}>
              {extraServices.map((item, index) => (
                <Link key={index} to={item.link} style={styles.exploreCardLink}>
                  <div style={styles.exploreCard}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={styles.exploreImage}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
                    />
                    <div style={styles.exploreCardBody}>
                      <h4 style={styles.exploreName}>{item.name}</h4>
                      <span style={styles.exploreLink}>Check Now →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>
      </div>

      {/* Details Modal */}
      <DetailsModal
        service={modalService}
        onClose={() => setModalService(null)}
        onAddToCart={handleBookNow}
        cartQty={modalService ? (getItem(modalService.id)?.qty || 0) : 0}
      />
    </div>
  );
};

// ─── Page styles ──────────────────────────────────────────────────────────────
const styles = {
  page:             { background: "#f8fafc", minHeight: "100vh", padding: "20px", fontFamily: "Arial, sans-serif", color: "#111827" },
  layout:           { maxWidth: "1300px", margin: "0 auto", display: "grid", gridTemplateColumns: "250px 1fr", gap: "20px", alignItems: "start" },
  sidebar:          { display: "flex", flexDirection: "column", gap: "18px" },
  helpCard:         { background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "18px", padding: "18px", textAlign: "left" },
  helpTitle:        { margin: "0 0 8px", fontSize: "18px", fontWeight: 700 },
  helpText:         { margin: "0 0 14px", fontSize: "14px", color: "#6b7280", lineHeight: 1.6 },
  chatBtn:          { border: "none", background: "#fff", color: "#4f46e5", fontWeight: 700, borderRadius: "12px", padding: "12px 16px", cursor: "pointer" },
  helpIcon:         { fontSize: "54px", textAlign: "center", marginTop: "18px" },
  main:             { minWidth: 0 },
  breadcrumb:       { fontSize: "13px", color: "#9ca3af", marginBottom: "14px" },
  sectionHeader:    { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  sectionTitle:     { margin: 0, fontSize: "24px", fontWeight: 700 },
  serviceCount:     { fontSize: "14px", color: "#6b7280" },
  // Minimized vs. the original 3-col/44px-button layout — most cards now
  // show just an image + title + two small buttons (duration-sold ones
  // moved their price/desc/stepper to InstantServiceDetailPage.js), so a
  // denser 4-col grid with smaller everything fits the actual content.
  serviceGrid:      { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px" },
  serviceCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", position: "relative" },
  qtyBadge:         { position: "absolute", top: "8px", right: "8px", background: "#4f46e5", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "999px" },
  serviceImage:     { width: "100%", aspectRatio: "4 / 3", objectFit: "cover" },
  serviceBody:      { padding: "10px" },
  serviceIcon:      { width: "26px", height: "26px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "6px", fontWeight: 700, fontSize: "13px" },
  serviceName:      { margin: "0 0 6px", fontSize: "14px", fontWeight: 700, lineHeight: 1.25 },
  serviceDesc:      { margin: "0 0 8px", fontSize: "12px", lineHeight: 1.5, color: "#6b7280" },
  serviceMeta:      { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" },
  price:            { fontSize: "15px", fontWeight: 800 },
  btnRow:           { display: "grid", gridTemplateColumns: "1fr auto", gap: "6px" },
  bookBtn:          { height: "34px", border: "none", background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", borderRadius: "9px", fontWeight: 700, fontSize: "12.5px", cursor: "pointer" },
  bookBtnAdded:     { background: "linear-gradient(135deg,#16a34a,#15803d)" },
  detailsBtn:       { height: "34px", padding: "0 10px", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#374151", borderRadius: "9px", fontWeight: 700, fontSize: "12.5px", cursor: "pointer", whiteSpace: "nowrap" },
  instantBadge:     { position: "absolute", top: "8px", left: "8px", background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", fontSize: "10px", fontWeight: 800, padding: "2px 8px", borderRadius: "999px", zIndex: 1 },

  // ── Featured (Instant CN Pro / CN Plus) — highlighted card ────────────────
  specialCard:      { border: "1.5px solid #f43f5e", boxShadow: "0 10px 28px rgba(244,63,94,0.14)" },
  specialRibbon:    { position: "absolute", top: "8px", left: "8px", background: "linear-gradient(135deg,#f43f5e,#e11d48)", color: "#fff", fontSize: "10px", fontWeight: 800, padding: "2px 8px", borderRadius: "999px", zIndex: 1 },
  specialBookBtn:   { background: "linear-gradient(135deg,#f43f5e,#e11d48)" },
  verifiedBadge:    { position: "absolute", top: "8px", right: "8px", background: "#ecfdf5", color: "#15803d", border: "1px solid #86efac", fontSize: "10px", fontWeight: 800, padding: "2px 8px", borderRadius: "999px", zIndex: 1 },
  gallerySectionTitle: { margin: "32px 0 16px", fontSize: "24px", fontWeight: 800 },
  galleryGrid:      { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "18px" },
  galleryCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", overflow: "hidden" },
  galleryImage:     { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" },
  galleryCaption:   { padding: "14px 16px", fontWeight: 700, fontSize: "15px" },
  bannerCard:       { marginTop: "22px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "16px", display: "grid", gridTemplateColumns: "180px 1fr auto", gap: "16px", alignItems: "center" },
  bannerImage:      { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: "14px" },
  bannerContent:    {},
  bannerTitle:      { margin: "0 0 6px", fontSize: "20px", fontWeight: 700, color: "#4f46e5" },
  bannerText:       { margin: "0 0 10px", fontSize: "14px", color: "#6b7280" },
  bannerTags:       { display: "flex", gap: "10px", flexWrap: "wrap", fontSize: "13px", color: "#4b5563" },
  bannerAction:     { textAlign: "right" },
  saveText:         { margin: "0 0 10px", fontSize: "13px", color: "#9ca3af" },
  bookNowBtn:       { border: "none", background: "#4f46e5", color: "#fff", height: "44px", padding: "0 18px", borderRadius: "12px", fontWeight: 700, cursor: "pointer" },
  exploreSection:   { marginTop: "24px" },
  exploreTitle:     { margin: "0 0 14px", fontSize: "24px", fontWeight: 700 },
  exploreGrid:      { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "12px" },
  exploreCardLink:  { textDecoration: "none", color: "inherit" },
  exploreCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", textAlign: "left" },
  exploreImage:     { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f3f4f6" },
  exploreCardBody:  { padding: "14px" },
  exploreName:      { margin: "0 0 10px", fontSize: "15px", fontWeight: 700 },
  exploreLink:      { color: "#4f46e5", fontWeight: 700, fontSize: "13px" },
};

// ─── Modal styles ─────────────────────────────────────────────────────────────
const M = {
  overlay:      { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", zIndex: 9999 },
  card:         { width: "min(860px,100%)", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: "22px", border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)", display: "flex", flexDirection: "column" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", padding: "20px 22px 14px", borderBottom: "1px solid #eef2ff" },
  title:        { margin: "0 0 6px", fontSize: "24px", fontWeight: 800, color: "#111827" },
  meta:         { display: "flex", alignItems: "center", gap: "8px" },
  price:        { fontSize: "20px", fontWeight: 800, color: "#4f46e5" },
  dot:          { color: "#94a3b8" },
  dur:          { fontSize: "14px", color: "#64748b", fontWeight: 600 },
  closeBtn:     { width: "36px", height: "36px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 800, flexShrink: 0 },
  sliderWrap:   { position: "relative", width: "100%", height: "240px", background: "#f1f5f9", overflow: "hidden", flexShrink: 0 },
  sliderImg:    { width: "100%", height: "100%", objectFit: "contain", display: "block" },
  arrow:        { position: "absolute", top: "50%", transform: "translateY(-50%)", width: "36px", height: "36px", borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: "22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, lineHeight: 1, zIndex: 2 },
  dots:         { position: "absolute", bottom: "10px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "6px", zIndex: 2 },
  dot2:         { width: "8px", height: "8px", borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.55)", cursor: "pointer", padding: 0 },
  dotActive:    { background: "#fff" },
  desc:         { padding: "14px 22px 0", fontSize: "14px", color: "#64748b", lineHeight: 1.7, margin: 0 },
  subServicesWrap: { padding: "12px 22px 0" },
  subServicesLabel: { fontSize: "12px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px" },
  subServicesRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
  subServiceChip:         { display: "inline-flex", alignItems: "center", gap: "4px", background: "#eef2ff", color: "#4338ca", fontSize: "12px", fontWeight: 600, padding: "4px 12px", borderRadius: "999px", border: "1px solid #c7d2fe" },
  grid:         { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", padding: "14px 22px" },
  panel:        { border: "1px solid #e2e8f0", borderRadius: "14px", padding: "16px", background: "#fbfcff" },
  panelHead:    { display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" },
  panelBadge:   { width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 800, flexShrink: 0 },
  panelTitle:   { margin: 0, fontSize: "15px", fontWeight: 800, color: "#111827" },
  list:         { margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "10px" },
  listItem:     { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.6 },
  listIcon:     { fontSize: "12px", color: "#16a34a", marginTop: "2px", flexShrink: 0 },
  notesBox:     { margin: "0 22px 18px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "12px", padding: "12px 14px" },
  notesLabel:   { fontSize: "12.5px", fontWeight: 800, color: "#92400e", marginBottom: "6px" },
  notesText:    { margin: 0, fontSize: "12.5px", color: "#78350f", lineHeight: 1.6 },
  notesList:    { margin: 0, padding: "0 0 0 16px", display: "flex", flexDirection: "column", gap: "6px" },
  notesListItem:{ fontSize: "12.5px", color: "#78350f", lineHeight: 1.6 },
  footer:       { padding: "14px 22px 20px", borderTop: "1px solid #eef2ff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" },
  qtyRow:       { display: "flex", alignItems: "center", gap: "10px" },
  inCartLabel:  { fontSize: "13px", fontWeight: 700, color: "#16a34a" },
  qtyBadge:     { fontSize: "13px", fontWeight: 700, color: "#374151" },
  addBtn:          { height: "46px", padding: "0 24px", border: "none", background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", borderRadius: "12px", fontWeight: 700, cursor: "pointer", fontSize: "14px" },
  addBtnGreen:     { background: "linear-gradient(135deg,#16a34a,#15803d)" },
};

export default HomeServices;
